import {
    CAPSULE_AND_TABLET_FORM_CODES,
    getPackageRelevance,
} from '@phx/rx-common';

import type {
    MedicationInfoFragment,
    MedicationPackageInfoFragment,
} from '../../graphql/generated/graphql';
import i18n from '../../i18n';

export const PACK_CODE_V2 = 'PA';
export const QUANTITY_OPTION_COUNT_V2 = 3;

function getPackUnitTotalTextV2(
    chosenQuantity: number,
    formPackage: Pick<
        MedicationPackageInfoFragment,
        | 'formCode'
        | 'packageCode'
        | 'packageQuantity'
        | 'packageSize'
        | 'packageSizeUnit'
    >
) {
    const { packageCode, packageQuantity, packageSize, formCode } = formPackage;

    const tPkg = i18n.getFixedT('en', 'code-locales/package-code-mapping');
    const tForm = i18n.getFixedT('en', 'code-locales/form-code-mapping');

    const packText = tPkg(PACK_CODE_V2, { count: chosenQuantity });
    const packageType = tPkg(packageCode, { count: packageQuantity });
    const formText = tForm(formCode, { count: packageQuantity });

    const total = packageSize * packageQuantity * chosenQuantity;

    const longText = `${chosenQuantity} ${packText} ${i18n.t('productConfig.of')} ${packageQuantity} ${packageType} (${total} ${formText})`;
    const shortText = `${total} ${formText}`;
    const formulaText = `${chosenQuantity} ${packText} * ${packageQuantity} ${packageType} * ${packageSize} ${formText} = ${total} ${formText}`;

    return {
        longText,
        shortText,
        formulaText,
        value: total,
        quantityDescriptor: packText,
    };
}

function getTotalPackageIrrelevantV2(
    chosenQuantity: number,
    formPackage: Pick<
        MedicationPackageInfoFragment,
        | 'formCode'
        | 'packageCode'
        | 'packageQuantity'
        | 'packageSize'
        | 'packageSizeUnit'
    >,
    routeOfAdministrationText: string
) {
    const { formCode, packageQuantity } = formPackage;

    if (packageQuantity && packageQuantity > 1) {
        //Pack Units
        return getPackUnitTotalTextV2(chosenQuantity, formPackage);
    }

    const tForm = i18n.getFixedT('en', 'code-locales/form-code-mapping');

    //Individual unit
    const formDescription = tForm(formCode, { count: chosenQuantity });
    const totalText = `${chosenQuantity} ${routeOfAdministrationText} ${formDescription}`;

    return {
        longText: totalText,
        shortText: totalText,
        formulaText: totalText,
        quantityDescriptor: formDescription,
        value: chosenQuantity,
    };
}

function getTotalPackageRelevantV2(
    chosenQuantity: number,
    formPackage: Pick<
        MedicationPackageInfoFragment,
        | 'formCode'
        | 'packageCode'
        | 'packageQuantity'
        | 'packageSize'
        | 'packageSizeUnit'
    >,
    routeOfAdministrationText: string
) {
    const {
        packageCode,
        packageQuantity,
        packageSize = 1,
        formCode,
    } = formPackage;

    if (packageQuantity && packageQuantity > 1) {
        //pack units
        return getPackUnitTotalTextV2(chosenQuantity, formPackage);
    }

    const tPkg = i18n.getFixedT('en', 'code-locales/package-code-mapping');
    const tForm = i18n.getFixedT('en', 'code-locales/form-code-mapping');

    const packageDescription = tPkg(packageCode, { count: chosenQuantity });
    const total = packageSize * chosenQuantity;
    const formDescription = tForm(formCode, { count: total });

    const longText = `${chosenQuantity} ${packageDescription} (${total} ${routeOfAdministrationText} ${formDescription})`;
    const shortText = `${total} ${formDescription}`;
    const formulaText = `${chosenQuantity} ${packageDescription} * ${packageSize} ${formDescription} = ${total} ${formDescription}`;

    return {
        longText,
        shortText,
        formulaText,
        quantityDescriptor: packageDescription,
        value: total,
    };
}

function getComplexTotalPackUnitV2(
    chosenQuantity: number,
    formPackage: Pick<
        MedicationPackageInfoFragment,
        | 'formCode'
        | 'packageCode'
        | 'packageQuantity'
        | 'packageSize'
        | 'packageSizeUnit'
    >,
    routeOfAdministrationText: string
) {
    const {
        packageCode,
        packageQuantity = 1,
        packageSize = 1,
        packageSizeUnit = '',
        formCode,
    } = formPackage;

    const tPkg = i18n.getFixedT('en', 'code-locales/package-code-mapping');
    const tForm = i18n.getFixedT('en', 'code-locales/form-code-mapping');

    const packText = tPkg(PACK_CODE_V2, { count: chosenQuantity });
    const packageDesc = tPkg(packageCode, { count: packageQuantity });

    const total = packageSize * packageQuantity * chosenQuantity;

    const formDesc = tForm(formCode, { count: 1 });

    const of = i18n.t('productConfig.of');

    const longText = `${chosenQuantity} ${packText} ${of} ${packageQuantity} ${packageDesc} ${of} ${routeOfAdministrationText} ${formDesc} (${total}${packageSizeUnit})`;
    const shortText = `${total}${packageSizeUnit}`;
    const formulaText = `${chosenQuantity} ${packText} * ${packageQuantity} ${packageDesc} * ${packageSize}${packageSizeUnit} = ${total}${packageSizeUnit} `;

    return {
        longText,
        shortText,
        formulaText,
        value: total,
        quantityDescriptor: packText,
    };
}

export function getTotalQuantityDescriptionV2(
    chosenQuantity: number,
    formPackage: Pick<
        MedicationPackageInfoFragment,
        | 'formCode'
        | 'packageCode'
        | 'packageQuantity'
        | 'packageSize'
        | 'packageSizeUnit'
    >,
    routeOfAdministrationText: string | null = ''
): {
    longText: string;
    shortText: string;
    formulaText: string;
    value: number;
    quantityDescriptor: string;
} {
    const {
        formCode,
        packageCode,
        packageQuantity,
        packageSize,
        packageSizeUnit,
    } = formPackage;

    const invalidROAValues = ['Does not apply', 'Unknown'];

    const isInvalidROA =
        !routeOfAdministrationText ||
        invalidROAValues.includes(routeOfAdministrationText);
    const validROA = isInvalidROA ? '' : routeOfAdministrationText;

    const formattedStrengthUnit = packageSizeUnit
        ? packageSizeUnit.toLowerCase()
        : '';

    //Simply Quantity
    if (formattedStrengthUnit === 'ea') {
        //Package not relevant
        if (
            CAPSULE_AND_TABLET_FORM_CODES.has(formCode) &&
            packageQuantity === 1 &&
            packageCode !== 'DP'
        ) {
            return getTotalPackageIrrelevantV2(
                chosenQuantity,
                formPackage,
                validROA
            );
        }
        return getTotalPackageRelevantV2(chosenQuantity, formPackage, validROA);
    }

    //Complex Quantity
    else if (formattedStrengthUnit === 'ml' || formattedStrengthUnit === 'gm') {
        if (packageQuantity && packageQuantity > 1) {
            return getComplexTotalPackUnitV2(
                chosenQuantity,
                formPackage,
                validROA
            );
        }

        const tPkg = i18n.getFixedT('en', 'code-locales/package-code-mapping');
        const tForm = i18n.getFixedT('en', 'code-locales/form-code-mapping');

        const total = packageSize * chosenQuantity;

        const packageDescription = tPkg(packageCode, { count: chosenQuantity });
        const formDescription = tForm(formCode, { count: 1 });

        const longText = i18n.t(
            'productConfig.totalQuantityDescriptions.longText',
            {
                chosenQuantity,
                packageDescription,
                formDescription,
                total,
                packageSizeUnit,
                routeOfAdministrationText: validROA,
            }
        );
        const shortText = `${total}${packageSizeUnit}`;
        const formulaText = `${chosenQuantity} ${packageDescription} * ${packageSize}${packageSizeUnit} = ${total}${packageSizeUnit}`;

        return {
            longText,
            shortText,
            formulaText,
            value: total,
            quantityDescriptor: packageDescription,
        };
    }

    return {
        longText: chosenQuantity.toString(),
        shortText: chosenQuantity.toString(),
        formulaText: chosenQuantity.toString(),
        quantityDescriptor: i18n.t('productConfig.quantity'),
        value: chosenQuantity,
    };
}

export function getPackageCodeQuantitiesV2(
    formPackage: Pick<
        MedicationInfoFragment,
        'packageCode' | 'defaultDaysSupply'
    >
) {
    const results = [];
    const modeDaysSuply = getPackageDaysSupplyV2(formPackage);

    const tPkg = i18n.getFixedT('en', 'code-locales/package-code-mapping');

    for (let i = 1; i <= QUANTITY_OPTION_COUNT_V2; i++) {
        results.push({
            displayText: `${i} ${tPkg(formPackage.packageCode, { count: i })}`,
            value: i.toString(),
            daysSupply: modeDaysSuply * i,
        });
    }

    return results;
}

export function getPackageDaysSupplyV2(
    formPackage?: Pick<MedicationInfoFragment, 'defaultDaysSupply'>
) {
    return formPackage?.defaultDaysSupply && formPackage.defaultDaysSupply > 0
        ? formPackage.defaultDaysSupply
        : 30;
}

export function getPackUnitQuantitiesV2(
    formPackage?: Pick<MedicationInfoFragment, 'defaultDaysSupply'>
) {
    const results = [];
    const modeDaysSuply = getPackageDaysSupplyV2(formPackage);

    const tPkg = i18n.getFixedT('en', 'code-locales/package-code-mapping');

    for (let i = 1; i <= QUANTITY_OPTION_COUNT_V2; i++) {
        results.push({
            displayText: `${i} ${tPkg(PACK_CODE_V2, { count: i })}`,
            value: i.toString(),
            daysSupply: modeDaysSuply * i,
        });
    }

    return results;
}

export function getIndividualFormQuantitiesV2(
    formPackage: Pick<
        MedicationInfoFragment,
        'defaultQuantity' | 'defaultDaysSupply' | 'formCode'
    >
) {
    const { defaultQuantity, defaultDaysSupply, formCode } = formPackage;
    const results = [];

    const quantity = 30; // always show 30, 60, 90 as options for individual form quantities
    const dosage = getDosage(defaultQuantity, defaultDaysSupply);
    const daysSupply = getDaysSupply(quantity, dosage);

    const tForm = i18n.getFixedT('en', 'code-locales/form-code-mapping');

    for (let i = 1; i <= QUANTITY_OPTION_COUNT_V2; i++) {
        const quantityOption = quantity * i;
        results.push({
            displayText: `${quantityOption} ${tForm(formCode, { count: quantityOption })}`,
            value: quantityOption.toString(),
            daysSupply: (i * daysSupply).toString(),
        });
    }

    return results;
}

/**
 * Get the quantity selection options to display for a given drug package
 */
export function getComplexQuantityOptionsV2(
    packageInfo: Pick<
        MedicationPackageInfoFragment,
        | 'defaultQuantity'
        | 'defaultDaysSupply'
        | 'packageQuantity'
        | 'packageSize'
        | 'packageCode'
    >
) {
    const {
        packageQuantity,
        packageCode,
        packageSize,
        defaultDaysSupply,
        defaultQuantity,
    } = packageInfo;

    const results = [];
    const quantityDescriptorCode =
        packageQuantity && packageQuantity > 1 ? PACK_CODE_V2 : packageCode;

    let baseQuantity = Math.ceil(
        (defaultQuantity ?? 1) / (packageSize ?? 1) / (packageQuantity ?? 1)
    );

    if (baseQuantity < 1) {
        baseQuantity = 1;
    }

    const tPkg = i18n.getFixedT('en', 'code-locales/package-code-mapping');

    for (let i = 1; i <= QUANTITY_OPTION_COUNT_V2; i++) {
        const quantity = baseQuantity * i;
        results.push({
            displayText: `${quantity} ${tPkg(quantityDescriptorCode, { count: quantity })}`,
            value: quantity.toString(),
            daysSupply: ((defaultDaysSupply ?? 1) * i).toString(),
        });
    }

    return results;
}

export function getTotalPackageQuantity(
    packageInfo: Pick<
        MedicationPackageInfoFragment,
        | 'defaultQuantity'
        | 'defaultDaysSupply'
        | 'packageQuantity'
        | 'packageSize'
        | 'packageCode'
        | 'formCode'
    >,
    chosenQuantity: number
) {
    const { packageSize, packageQuantity } = packageInfo;
    let totalQuantity = chosenQuantity * packageSize;

    if (packageQuantity > 1) {
        totalQuantity = totalQuantity * packageQuantity;
    }

    const packageRelevance = getPackageRelevance(packageInfo);

    if (
        packageRelevance?.isSimpleQuantity &&
        !packageRelevance.isPackageRelevant
    ) {
        totalQuantity = chosenQuantity;
    }

    return totalQuantity;
}

export function getDosage(quantity: number, daysSupply: number) {
    return quantity / daysSupply;
}

export function getDaysSupply(quantity: number, dosage: number) {
    return Math.ceil(quantity / dosage);
}

export function getCustomQuantityDaysSupply(
    packageInfo: Pick<
        MedicationPackageInfoFragment,
        | 'defaultQuantity'
        | 'defaultDaysSupply'
        | 'packageQuantity'
        | 'packageSize'
        | 'packageCode'
        | 'formCode'
    >,
    chosenQuantity: number
) {
    const { defaultQuantity, defaultDaysSupply } = packageInfo;
    const dosage = getDosage(defaultQuantity, defaultDaysSupply);
    const quantity = getTotalPackageQuantity(packageInfo, chosenQuantity);

    const daysSupply = getDaysSupply(quantity, dosage);

    // Return 180 if the result is greater than 180, otherwise return the result
    if (daysSupply > 180) {
        return 180;
    }

    return daysSupply;
}

export function computeDaysSupplyV2(
    packageInfo: Pick<
        MedicationPackageInfoFragment,
        | 'defaultQuantity'
        | 'defaultDaysSupply'
        | 'packageQuantity'
        | 'packageSize'
        | 'packageCode'
        | 'formCode'
        | 'quantityOptions'
    >,
    chosenQuantity: number
) {
    const { quantityOptions } = packageInfo;

    const matchingQuantityOption = quantityOptions.find(
        (option) => Number(option.value) === chosenQuantity
    );

    if (matchingQuantityOption && !!matchingQuantityOption.daysSupply) {
        return Number(matchingQuantityOption.daysSupply);
    } else if (quantityOptions.length) {
        return (
            quantityOptions.find(
                (option) => chosenQuantity === Number(option.value)
            )?.daysSupply ??
            getCustomQuantityDaysSupply(packageInfo, chosenQuantity)
        );
    }

    return 30; //fallback days supply
}

export function getStrengthValue(value?: string, unit?: string) {
    if (!value || !unit || value === '-') {
        return '-';
    }

    return `${value}${unit}`;
}
